import { useEffect, useState } from "react";
import { Search, AlertTriangle, CheckCircle2, XCircle, Clock, DollarSign } from "lucide-react";
import api from "@/lib/api";

const typeLabels = { one_time: "Único", recurring: "Recorrente", manual: "Manual" };

export default function PaymentList() {
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const { data } = await api.get("/payments", { params });
      setPayments(data);
    } catch { /* */ }
    setLoading(false);
  };

  useEffect(() => { fetchPayments(); }, [statusFilter]);
  const handleSearch = (e) => { e.preventDefault(); fetchPayments(); };

  const markPaid = async (id) => {
    const today = new Date().toISOString().slice(0, 10);
    await api.put(`/payments/${id}`, { status: "paid", payment_date: today });
    fetchPayments();
  };

  const today = new Date().toISOString().slice(0, 10);

  // Summary stats
  const pendingCount = payments.filter((p) => p.status === "pending").length;
  const overdueCount = payments.filter((p) => p.status === "pending" && p.due_date < today).length;
  const paidCount = payments.filter((p) => p.status === "paid").length;
  const pendingAmount = payments.filter((p) => p.status === "pending").reduce((s, p) => s + p.amount, 0);

  const StatusBadge = ({ status, dueDate }) => {
    const isOverdue = status === "pending" && dueDate < today;
    if (isOverdue) return (
      <span data-testid="status-overdue" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: "rgba(239,68,68,0.15)", color: "#ef4444", border: "1.5px solid rgba(239,68,68,0.3)" }}>
        <AlertTriangle className="w-3.5 h-3.5" /> ATRASADO
      </span>
    );
    const config = {
      pending: { icon: Clock, label: "PENDENTE", bg: "rgba(249,115,22,0.12)", color: "#f97316", border: "rgba(249,115,22,0.25)" },
      paid: { icon: CheckCircle2, label: "PAGO", bg: "rgba(16,185,129,0.12)", color: "#10b981", border: "rgba(16,185,129,0.25)" },
      failed: { icon: XCircle, label: "FALHOU", bg: "rgba(239,68,68,0.12)", color: "#ef4444", border: "rgba(239,68,68,0.25)" },
      refunded: { icon: DollarSign, label: "REEMBOLSADO", bg: "rgba(148,163,184,0.12)", color: "#94a3b8", border: "rgba(148,163,184,0.25)" },
    };
    const c = config[status] || config.pending;
    const Icon = c.icon;
    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold" style={{ background: c.bg, color: c.color, border: `1.5px solid ${c.border}` }}>
        <Icon className="w-3.5 h-3.5" /> {c.label}
      </span>
    );
  };

  return (
    <div data-testid="payment-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="crm-heading">Pagamentos</h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(249,115,22,0.1)", color: "#f97316" }}><Clock className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>{pendingCount}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Pendentes</p></div>
        </div>
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(239,68,68,0.1)", color: "#ef4444" }}><AlertTriangle className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: overdueCount > 0 ? "#ef4444" : "var(--crm-text)" }}>{overdueCount}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Atrasados</p></div>
        </div>
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(16,185,129,0.1)", color: "#10b981" }}><CheckCircle2 className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>{paidCount}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Pagos</p></div>
        </div>
        <div className="crm-card px-4 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: "rgba(245,158,11,0.1)", color: "#f59e0b" }}><DollarSign className="w-5 h-5" /></div>
          <div><p className="text-xl font-bold" style={{ color: "var(--crm-text)" }}>¥{pendingAmount.toLocaleString()}</p><p className="text-xs" style={{ color: "var(--crm-text-muted)" }}>Valor Pendente</p></div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <form onSubmit={handleSearch} className="flex-1 relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--crm-text-dim)" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="payment-search"
            placeholder="Buscar por cliente..." className="crm-input pl-10" />
        </form>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="payment-status-filter"
          className="crm-input" style={{ maxWidth: "200px" }}>
          <option value="">Todos os status</option>
          <option value="pending">Pendente</option>
          <option value="paid">Pago</option>
          <option value="failed">Falhou</option>
          <option value="refunded">Reembolsado</option>
        </select>
      </div>

      {/* Table */}
      <div className="crm-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="crm-table">
            <thead><tr><th>Cliente</th><th>Valor</th><th>Tipo</th><th>Vencimento</th><th>Pagamento</th><th>Status</th><th className="text-right">Ações</th></tr></thead>
            <tbody>
              {loading ? (
                [...Array(3)].map((_, i) => <tr key={i}><td colSpan={7}><div className="h-6 rounded animate-pulse" style={{ background: "var(--crm-border)" }} /></td></tr>)
              ) : payments.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-14 text-base" style={{ color: "var(--crm-text-dim)" }}>Nenhum pagamento encontrado</td></tr>
              ) : (
                payments.map((p) => {
                  const isOverdue = p.status === "pending" && p.due_date < today;
                  return (
                    <tr key={p.id} data-testid={`payment-row-${p.id}`}
                      style={{ background: isOverdue ? "rgba(239,68,68,0.04)" : undefined }}>
                      <td className="primary" style={{ fontSize: "15px" }}>{p.customer_name}</td>
                      <td style={{ color: "var(--crm-text)", fontWeight: 700, fontSize: "16px" }}>¥{p.amount.toLocaleString()}</td>
                      <td>
                        <span className="inline-flex px-2.5 py-1 rounded-md text-[11px] font-semibold"
                          style={{ background: "rgba(59,130,246,0.08)", color: "#3b82f6", border: "1px solid rgba(59,130,246,0.15)" }}>
                          {typeLabels[p.type] || p.type}
                        </span>
                      </td>
                      <td>
                        <span className="flex items-center gap-1.5 text-sm font-medium"
                          style={{ color: isOverdue ? "#ef4444" : "var(--crm-text-secondary)" }}>
                          {isOverdue && <AlertTriangle className="w-4 h-4" />}
                          {p.due_date}
                        </span>
                      </td>
                      <td style={{ color: p.payment_date ? "#10b981" : "var(--crm-text-dim)", fontWeight: p.payment_date ? 500 : 400 }}>{p.payment_date || "—"}</td>
                      <td><StatusBadge status={p.status} dueDate={p.due_date} /></td>
                      <td className="text-right">
                        {p.status === "pending" && (
                          <button onClick={() => markPaid(p.id)} data-testid={`mark-paid-${p.id}`}
                            className="inline-flex items-center gap-2 text-sm font-bold px-4 py-2.5 rounded-xl transition-all hover:shadow-lg"
                            style={{ color: "white", background: "#10b981", boxShadow: "0 2px 8px rgba(16,185,129,0.25)" }}>
                            <CheckCircle2 className="w-4 h-4" /> Pago
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <p className="text-xs mt-3" style={{ color: "var(--crm-text-dim)" }}>{payments.length} pagamento(s)</p>
    </div>
  );
}
